import React, { useState } from 'react';
import { CheckCircle2, Circle, Upload, FileText, AlertCircle } from 'lucide-react';
export function AssessmentPage() {
  const [currentStep, setCurrentStep] = useState(2); // 1-indexed, so 2 is Business Diagnostics
  const steps = [{
    id: 1,
    title: 'Founder Profile',
    status: 'completed'
  }, {
    id: 2,
    title: 'Business Diagnostics',
    status: 'current'
  }, {
    id: 3,
    title: 'Growth Goals',
    status: 'upcoming'
  }, {
    id: 4,
    title: 'Document Upload',
    status: 'upcoming'
  }];
  return <div className="space-y-8 animate-in fade-in duration-500 max-w-4xl mx-auto" data-id="element-195">
      <div className="text-center mb-10" data-id="element-196">
        <h1 className="text-3xl font-heading font-bold text-catalyst-black tracking-tight" data-id="element-197">
          Needs Assessment
        </h1>
        <p className="text-gray-500 mt-2 max-w-2xl mx-auto" data-id="element-198">
          Help us tailor the accelerator experience to your specific business
          needs. This information is confidential and shared only with your lead
          advisor.
        </p>
      </div>

      {/* Progress Tracker */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8" data-id="element-199">
        <div className="flex justify-between items-center relative" data-id="element-200">
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-gray-100 rounded-full z-0" data-id="element-201"></div>
          <div className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-catalyst-mustard rounded-full z-0 transition-all duration-500" style={{
          width: '33%'
        }} data-id="element-202"></div>

          {steps.map((step, idx) => <div key={step.id} className="relative z-10 flex flex-col items-center gap-2" data-id="element-203">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-colors shadow-sm ${step.status === 'completed' ? 'bg-catalyst-mustard text-white' : step.status === 'current' ? 'bg-white border-2 border-catalyst-mustard text-catalyst-mustard-dark' : 'bg-white border-2 border-gray-200 text-gray-400'}`} data-id="element-204">
                {step.status === 'completed' ? <CheckCircle2 size={20} data-id="element-205" /> : step.id}
              </div>
              <span className={`text-xs font-semibold uppercase tracking-wider hidden sm:block ${step.status === 'completed' ? 'text-catalyst-black' : step.status === 'current' ? 'text-catalyst-mustard-dark' : 'text-gray-400'}`} data-id="element-206">
                {step.title}
              </span>
            </div>)}
        </div>
      </div>

      {/* Form Content */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-207">
        <div className="p-8 border-b border-gray-100" data-id="element-208">
          <h2 className="text-2xl font-bold text-catalyst-black mb-1" data-id="element-209">
            Business Diagnostics
          </h2>
          <p className="text-sm text-gray-500" data-id="element-210">
            Current operational metrics and challenges.
          </p>
        </div>

        <div className="p-8 space-y-8" data-id="element-211">
          {/* Section 1 */}
          <div className="space-y-5" data-id="element-212">
            <h3 className="text-lg font-semibold text-catalyst-black border-b border-gray-100 pb-2" data-id="element-213">
              Financial Overview
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-214">
              <div data-id="element-215">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-216">
                  Current Monthly Recurring Revenue (MRR)
                </label>
                <div className="relative" data-id="element-217">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" data-id="element-218">
                    $
                  </span>
                  <input type="text" className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-catalyst-mustard focus:border-catalyst-mustard text-sm" placeholder="e.g. 15,000" defaultValue="25,000" data-id="element-219" />
                </div>
              </div>
              <div data-id="element-220">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-221">
                  Target MRR (in 6 months)
                </label>
                <div className="relative" data-id="element-222">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" data-id="element-223">
                    $
                  </span>
                  <input type="text" className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-catalyst-mustard focus:border-catalyst-mustard text-sm" placeholder="e.g. 50,000" defaultValue="50,000" data-id="element-224" />
                </div>
              </div>
            </div>

            <div data-id="element-225">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-226">
                Current Funding Stage
              </label>
              <select className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-catalyst-mustard focus:border-catalyst-mustard text-sm bg-white" data-id="element-227">
                <option data-id="element-228">Bootstrapped</option>
                <option data-id="element-229">Pre-Seed</option>
                <option selected data-id="element-230">Seed</option>
                <option data-id="element-231">Series A</option>
              </select>
            </div>
          </div>

          {/* Section 2 */}
          <div className="space-y-5 pt-4" data-id="element-232">
            <h3 className="text-lg font-semibold text-catalyst-black border-b border-gray-100 pb-2" data-id="element-233">
              Operational Challenges
            </h3>

            <div data-id="element-234">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-235">
                What is your biggest bottleneck to growth right now?
              </label>
              <textarea className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-catalyst-mustard focus:border-catalyst-mustard text-sm min-h-[100px]" placeholder="Describe your main challenges..." defaultValue="Customer acquisition cost is too high on current paid channels. We need to diversify our go-to-market strategy and build a more robust inbound pipeline." data-id="element-236"></textarea>
            </div>

            <div data-id="element-237">
              <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-238">
                Select areas where you need the most support (Select up to 3)
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-239">
                {['Financial Modeling', 'Go-to-Market Strategy', 'Fundraising / Pitching', 'Hiring & Culture', 'Product Development', 'Sales Operations'].map((area, idx) => <label key={idx} className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${idx === 1 || idx === 2 ? 'border-catalyst-mustard bg-catalyst-mustard/5' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-240">
                    <input type="checkbox" className="w-4 h-4 text-catalyst-mustard focus:ring-catalyst-mustard border-gray-300 rounded" defaultChecked={idx === 1 || idx === 2} data-id="element-241" />
                    <span className="text-sm font-medium text-gray-700" data-id="element-242">
                      {area}
                    </span>
                  </label>)}
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="bg-gray-50 p-6 border-t border-gray-100 flex justify-between items-center" data-id="element-243">
          <button className="px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-white transition-colors shadow-sm" data-id="element-244">
            Back
          </button>
          <div className="flex items-center gap-4" data-id="element-245">
            <span className="text-sm text-gray-500 flex items-center gap-1" data-id="element-246">
              <AlertCircle size={14} data-id="element-247" /> Auto-saved 2 mins ago
            </span>
            <button className="bg-catalyst-mustard text-catalyst-black px-8 py-2.5 rounded-lg text-sm font-semibold hover:bg-catalyst-mustard-light transition-colors shadow-sm" data-id="element-248">
              Save & Continue
            </button>
          </div>
        </div>
      </div>
    </div>;
}